import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FileSignature, ChevronDown, MailQuestion, ReceiptText, Sparkles } from 'lucide-react';
import { type InquiryStatus } from '@vendorops/shared';
import { client } from '@/lib/api';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import StatusChip from '@/components/StatusChip';
import SetupCard from '@/components/SetupCard';
import { formatDate } from '@/lib/date';
import { formatQuote } from '@/lib/format';

/**
 * The cross-wedding action dashboard (PRD §F7) — "the one screen that replaces
 * the anxiety", and the post-login default route.
 *
 * One question, asked three ways: *what did a vendor send me that I have not
 * dealt with?* Contracts commit money, a quote wants a decision, and everything
 * else is a message still unanswered — that is the order, and it is the whole
 * page.
 *
 * The sections do not overlap: the backend withholds from New messages anything
 * the sections above it already claimed, so one waiting vendor is one row here
 * and not three. Within a section, ordering is what will bite first —
 * wedding-date proximity, then how long the vendor has been waiting.
 */

type Item = {
  inquiryId: string;
  vendorName: string;
  coupleNames: string;
  weddingId: string;
  weddingDate: string | null;
  category: string;
  status: string;
  statusSource: string;
  daysUntilWedding: number;
  daysWaiting: number;
  quoteAmount: string | null;
  quoteAmountHigh: string | null;
  quoteCurrency: string;
  quoteSource: string | null;
};

/** Section open/closed state survives reloads — PRD §F7 asks them to remember. */
function useCollapsed(key: string) {
  const storageKey = `vendorops.dashboard.${key}`;
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(storageKey) === 'collapsed',
  );
  useEffect(() => {
    localStorage.setItem(storageKey, collapsed ? 'collapsed' : 'open');
  }, [collapsed, storageKey]);
  return [collapsed, () => setCollapsed((v) => !v)] as const;
}

/** "2d ago" reads wrong on the day it happened, and "0d ago" reads like a bug. */
function ago(days: number) {
  return days === 0 ? 'today' : `${days}d ago`;
}

/**
 * How many rows this page would show, for the Inbox badge in the global nav.
 *
 * A separate endpoint rather than a sum over the feed, because Layout renders
 * on every screen and the feed is four queries and a wedding list. Keyed under
 * `['dashboard', ...]` on purpose: every existing `invalidateQueries({ queryKey:
 * ['dashboard'] })` in the app already matches this by prefix, so nothing had
 * to be taught about the badge for it to stay honest after a mutation.
 */
export function useInboxCount() {
  return useQuery({
    queryKey: ['dashboard', 'count'],
    queryFn: async () => {
      const res = await client.api.dashboard.count.$get();
      if (!res.ok) throw new Error('Failed to load the inbox count');
      return res.json();
    },
    refetchInterval: 60_000,
  });
}

export default function DashboardPage() {
  const [weddingId, setWeddingId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', weddingId],
    queryFn: async () => {
      const res = await client.api.dashboard.$get({ query: { weddingId } });
      if (!res.ok) throw new Error('Failed to load the dashboard');
      return res.json();
    },
    refetchInterval: 60_000,
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data) return null;

  const filtered = !!weddingId;

  /**
   * `Item` is hand-written, so these three annotations are what ties it to the
   * response. Without them a field the route renamed or stopped sending would
   * reach `Row` as `undefined` and render — `quoteCurrency` most quietly of
   * all, since a missing one prints the word rather than throwing.
   */
  const contracts: Item[] = data.contractsReceived;
  const quotes: Item[] = data.quotesReceived;
  const newMessages: Item[] = data.newMessages;

  return (
    <div className="space-y-8">
      {/* Above the feed: unfinished setup is the reason the feed may be empty
          and the reason a send would fail, so it outranks what follows. */}
      <SetupCard />

      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Named the same as the nav tab that routes here. A heading that
            renamed the destination on arrival made the planner check they had
            landed where they clicked. */}
        <h1 className="display-tight text-3xl font-semibold">Inbox</h1>
        <div className="flex flex-wrap gap-2">
          <Select value={weddingId || 'all'} onValueChange={(v) => setWeddingId(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-9 w-52">
              <SelectValue placeholder="All weddings" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All weddings</SelectItem>
              {data.weddingOptions.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.coupleNames}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* The empty state is the goal, not a fallback. An inbox that never
          empties teaches learned helplessness (PRD §F7). */}
      {data.caughtUp ? (
        <Card className="rounded-3xl bg-card p-14 text-center shadow-ambient">
          <Sparkles className="mx-auto size-8 text-primary" />
          <p className="display-tight mt-4 text-xl font-semibold">You're caught up</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {filtered
              ? 'Nothing outstanding for this filter.'
              : 'Nothing a vendor sent you is still waiting.'}
          </p>
        </Card>
      ) : (
        <>
          <Section
            id="contracts-received"
            title="Contracts received"
            hint="A vendor sent paperwork to sign"
            icon={<FileSignature className="size-4" />}
            count={contracts.length}
          >
            {contracts.map((item) => (
              <Row key={item.inquiryId} item={item}>
                <span className="text-xs text-muted-foreground">sent {ago(item.daysWaiting)}</span>
              </Row>
            ))}
          </Section>

          <Section
            id="quotes-received"
            title="Quotes received"
            icon={<ReceiptText className="size-4" />}
            count={quotes.length}
          >
            {quotes.map((item) => (
              <Row key={item.inquiryId} item={item}>
                <span className="text-xs text-muted-foreground">quoted {ago(item.daysWaiting)}</span>
              </Row>
            ))}
          </Section>

          <Section
            id="new-messages"
            title="New messages"
            icon={<MailQuestion className="size-4" />}
            count={newMessages.length}
          >
            {newMessages.map((item) => (
              <Row key={item.inquiryId} item={item}>
                <span className="text-xs text-muted-foreground">replied {ago(item.daysWaiting)}</span>
              </Row>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The sections used to carry a `tone` that only ever recoloured the count
 * badge, on the theory that one kind of waiting was more alarming than
 * another. Three sections that all mean "a vendor is waiting on you" do not
 * need three colours to say so — the order and the title already carry which
 * is which, and a second, weaker signal disagreeing with the first is how a
 * badge stops being read. One badge, one meaning: how many.
 */
function Section({
  id,
  title,
  hint,
  icon,
  count,
  children,
}: {
  id: string;
  title: string;
  /** Omitted where the title already says it — "New messages" needs no gloss,
   *  and a subtitle restating the heading trains the eye to skip both. */
  hint?: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
}) {
  const [collapsed, toggle] = useCollapsed(id);
  if (count === 0) return null;

  return (
    <Card className="rounded-3xl bg-card p-5 shadow-ambient">
      <button className="flex w-full items-center gap-3 text-left" onClick={toggle}>
        {icon}
        <div className="flex-1">
          <h2 className="display-tight text-lg font-semibold">
            {title}
            <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-semibold leading-none text-primary-foreground">
              {count}
            </span>
          </h2>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <ChevronDown className={`size-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
      </button>
      {!collapsed && <div className="mt-3 divide-y divide-border">{children}</div>}
    </Card>
  );
}

/**
 * One actionable line. PRD §F7: every item is one click from the inquiry.
 *
 * The wedding leads, not the vendor. A planner scanning this page is holding
 * couples in their head, not businesses — "what is going on with the Smiths"
 * is the question, and a column of vendor names made them read the second line
 * of every row to answer it. The date rides on the same line because it
 * qualifies the wedding rather than the message.
 *
 * No "(84d away)" beside it. The countdown was arithmetic the reader can do
 * from the date, on every row, forever — and it was redundant with the thing
 * it was meant to convey: `daysUntilWedding` is the feed's primary sort key, so
 * the soonest wedding is already at the top. Order says it once; a number on
 * every row said it forty times. The field stays in the payload because the
 * sort runs on it server-side.
 */
function Row({ item, children }: { item: Item; children?: React.ReactNode }) {
  return (
    <Link
      to={`/inquiries/${item.inquiryId}`}
      className="flex flex-wrap items-center justify-between gap-3 py-3 transition-colors hover:bg-[var(--color-surface-container-high)]"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {item.coupleNames}
          {item.weddingDate && (
            <span className="ml-2 font-normal text-muted-foreground">
              {formatDate(item.weddingDate)}
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {item.vendorName}
          {' · '}
          {item.category}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {children}
        {item.quoteAmount && (
          <span className="text-sm font-medium whitespace-nowrap">
            {formatQuote({
              amount: item.quoteAmount,
              amountHigh: item.quoteAmountHigh,
              currency: item.quoteCurrency,
            })}
          </span>
        )}
        <StatusChip
          status={item.status as InquiryStatus}
          source={item.statusSource as 'system' | 'user'}
        />
      </div>
    </Link>
  );
}
